import type React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../store";
import { getErrorMessage } from "../../utils/errorHandling";
import { toastEmitter } from "../../utils/toastEmitter";
import type { ThunkDispatch, AsyncThunk, AnyAction } from "@reduxjs/toolkit";
import type translations from "../../translations/translations.json";
import { isUserAdmin, UserRole } from "../../types/user";

type TranslationKeys = keyof typeof translations.en;

interface SimpleEntity {
  _id: string;
  name: string;
}

interface SimpleCrudPageProps<T extends SimpleEntity> {
  titleTranslationKey: string;
  addButtonTranslationKey: string;
  fetchAction: AsyncThunk<T[], void, { rejectValue: string }>;
  createApi: (data: { name: string }) => Promise<unknown>;
  updateApi: (id: string, data: { name: string }) => Promise<unknown>;
  selector: (state: RootState) => { items: T[]; loading: boolean };
  createErrorContext: string;
  updateErrorContext: string;
  createSuccessMessage: string;
  updateSuccessMessage: string;
  TableComponent: React.ComponentType<{
    items: T[];
    loading?: boolean;
    onEdit?: (id: string, newName: string) => Promise<void>;
    canEdit?: boolean;
  }>;
  FormComponent: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string }) => Promise<void>;
    error: string | null;
  }>;
}

function SimpleCrudPage<T extends SimpleEntity>({
  titleTranslationKey,
  addButtonTranslationKey,
  fetchAction,
  createApi,
  updateApi,
  selector,
  createErrorContext,
  updateErrorContext,
  createSuccessMessage,
  updateSuccessMessage,
  TableComponent,
  FormComponent,
}: SimpleCrudPageProps<T>) {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const { items, loading } = useSelector(selector);
  const { user } = useSelector((state: RootState) => state.user);
  const isAdmin = isUserAdmin(user);
  const isAdminOrStaff = isAdmin || user?.role === UserRole.STAFF;

  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAction() as any);
  }, [dispatch, fetchAction]);

  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setAddError(null);
  };

  const handleSubmit = async (data: { name: string }) => {
    try {
      await createApi(data);
      await dispatch(fetchAction() as any).unwrap();
      handleClose();
      toastEmitter.emit(t(createSuccessMessage as TranslationKeys), "success");
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, createErrorContext);
      setAddError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  const handleEdit = async (id: string, newName: string) => {
    try {
      await updateApi(id, { name: newName });
      await dispatch(fetchAction() as any).unwrap();
      toastEmitter.emit(t(updateSuccessMessage as TranslationKeys), "success");
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, updateErrorContext);
      toastEmitter.emit(errorMessage, "error");
      throw err;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{t(titleTranslationKey as TranslationKeys)}</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          disabled={!isAdminOrStaff}
        >
          {t(addButtonTranslationKey as TranslationKeys)}
        </Button>
      </Box>
      <TableComponent
        items={items}
        onEdit={isAdminOrStaff ? handleEdit : undefined}
        canEdit={isAdminOrStaff}
      />
      <FormComponent open={open} onClose={handleClose} onSubmit={handleSubmit} error={addError} />
    </Box>
  );
}

export default SimpleCrudPage;
